import React, { useEffect, useState } from "react";
import "@fontsource/rajdhani/700.css";
import {
  Box,
  Container,
  Heading,
  Stack,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from "@chakra-ui/react";

const PLAYFAB_TITLE_ID = "186E06";
const PLAYFAB_API_ENDPOINT = "https://186E06.playfabapi.com";
const LEADERBOARD_STAT_NAME = "DesertDemoBestRunTimeV1";

const formatTime = (centiseconds) => {
  if (centiseconds == null) return "--:--";

  // Stored as negative centiseconds
  const cs = Math.abs(centiseconds);

  const totalSeconds = Math.round(cs / 100);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalShown, setTotalShown] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        const loginResponse = await fetch(
          `${PLAYFAB_API_ENDPOINT}/Client/LoginWithCustomID`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              TitleId: PLAYFAB_TITLE_ID,
              CustomId: "website-leaderboard-viewer",
              CreateAccount: true,
            }),
          }
        );

        const loginData = await loginResponse.json();
        console.log("Login Response:", loginData);

        if (!loginResponse.ok || loginData.error) {
          throw new Error(loginData.errorMessage || "PlayFab login failed.");
        }

        const sessionTicket = loginData.data.SessionTicket;

        const leaderboardResponse = await fetch(
          `${PLAYFAB_API_ENDPOINT}/Client/GetLeaderboard`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Authorization": sessionTicket,
            },
            body: JSON.stringify({
              StatisticName: LEADERBOARD_STAT_NAME,
              StartPosition: 0,
              MaxResultsCount: 100,
            }),
          }
        );

        const leaderboardData = await leaderboardResponse.json();
        console.log("Leaderboard Response:", leaderboardData);

        if (!leaderboardResponse.ok || leaderboardData.error) {
          throw new Error(
            leaderboardData.errorMessage || "Leaderboard request failed."
          );
        }

        const entries = leaderboardData.data.Leaderboard || [];

        setLeaderboard(entries);
        setTotalShown(entries.length);
      } catch (err) {
        console.error("PlayFab Error:", err);
        setError(err.message || "Could not load leaderboard.");
      } finally {
        setLoading(false);
      }
    };

    loadLeaderboard();
  }, []);

  return (
    <Box bg="#0A0A0A" color="white" minH="100vh">
      <Box
        position="relative"
        py={{ base: 16, md: 32 }}
        backgroundImage="url('/images/HighresScreenshot00005.png')"
        backgroundSize="cover"
        backgroundPosition="center"
        alt="Velocity Protocol Desert Demo Map Image"

      >
        <Box position="absolute" inset={0} bg="rgba(0,0,0,0.5)" />

        <Container maxW="container.lg" position="relative" zIndex={1} px={{ base: 4, md: 6 }}>
          <Stack spacing={{ base: 4, md: 5 }} textAlign="center">
            <Text
              fontSize={{ base: "xs", md: "sm" }}
              letterSpacing={{ base: "0.14em", md: "0.25em" }}
              textTransform="uppercase"
              color="gray.400"
            >
              Global Rankings
            </Text>

            <Heading
              fontSize={{ base: "4xl", md: "7xl" }}
              lineHeight="0.95"
              fontWeight="700"
              fontFamily="'Rajdhani', sans-serif"
              textTransform="uppercase"
            >
              Best Times
            </Heading>

            <Text
              maxW="650px"
              mx="auto"
              color="gray.300"
              fontSize={{ base: "md", md: "lg" }}
            >
              The fastest players in Velocity Protocol. Showing the top 100
              submitted runs when available.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 10, md: 20 }} borderTop="1px solid rgba(255,255,255,0.08)">
        <Container maxW="container.lg" px={{ base: 4, md: 6 }}>
          <Box
            bg="rgba(255,255,255,0.035)"
            border="1px solid rgba(255,255,255,0.08)"
            borderRadius={{ base: "14px", md: "20px" }}
            overflow="hidden"
          >
            <Box
              px={{ base: 5, md: 8 }}
              py={6}
              borderBottom="1px solid rgba(255,255,255,0.08)"
            >
              <Heading
                fontSize={{ base: "3xl", md: "4xl" }}
                fontFamily="'Rajdhani', sans-serif"
                textTransform="uppercase"
              >
                Top Runs
              </Heading>

              {!loading && !error && (
                <Text color="gray.400" mt={2}>
                  Showing {totalShown} {totalShown === 1 ? "entry" : "entries"}.
                </Text>
              )}
            </Box>

            {loading && (
              <Stack align="center" justify="center" py={{ base: 12, md: 20 }}>
                <Spinner size="xl" />
                <Text color="gray.400">Loading leaderboard...</Text>
              </Stack>
            )}

            {!loading && error && (
              <Box p={{ base: 5, md: 8 }}>
                <Text color="red.300">{error}</Text>
              </Box>
            )}

            {!loading && !error && leaderboard.length === 0 && (
              <Box p={{ base: 5, md: 8 }}>
                <Text color="gray.400">
                  No leaderboard times have been submitted yet.
                </Text>
              </Box>
            )}

            {!loading && !error && leaderboard.length > 0 && (
              <Stack display={{ base: "flex", md: "none" }} spacing={3} p={4}>
                {leaderboard.map((entry) => (
                  <Box
                    key={entry.PlayFabId}
                    bg="rgba(255,255,255,0.04)"
                    border="1px solid rgba(255,255,255,0.08)"
                    borderRadius="12px"
                    p={4}
                  >
                    <Stack spacing={3}>
                      <Box>
                        <Badge
                          bg={
                            entry.Position === 0
                              ? "yellow.400"
                              : "whiteAlpha.200"
                          }
                          color={entry.Position === 0 ? "black" : "white"}
                        >
                          #{entry.Position + 1}
                        </Badge>
                      </Box>

                      <Box>
                        <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="0.12em">
                          Player
                        </Text>
                        <Text fontWeight="bold" overflowWrap="anywhere">
                          {entry.DisplayName || "Unknown Player"}
                        </Text>
                      </Box>

                      <Box>
                        <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="0.12em">
                          Time
                        </Text>
                        <Text fontFamily="'Rajdhani', sans-serif" fontSize="2xl" fontWeight="700">
                          {formatTime(entry.StatValue)}
                        </Text>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}

            {!loading && !error && leaderboard.length > 0 && (
              <Box display={{ base: "none", md: "block" }} overflowX="auto">
                <Table variant="simple">
                  <Thead bg="rgba(255,255,255,0.04)">
                    <Tr>
                      <Th color="gray.400">Rank</Th>
                      <Th color="gray.400">Player</Th>
                      <Th color="gray.400" isNumeric>
                        Time
                      </Th>
                    </Tr>
                  </Thead>

                  <Tbody>
                    {leaderboard.map((entry) => (
                      <Tr key={entry.PlayFabId}>
                        <Td>
                          <Badge
                            bg={
                              entry.Position === 0
                                ? "yellow.400"
                                : "whiteAlpha.200"
                            }
                            color={entry.Position === 0 ? "black" : "white"}
                          >
                            #{entry.Position + 1}
                          </Badge>
                        </Td>

                        <Td fontWeight="bold">
                          {entry.DisplayName || "Unknown Player"}
                        </Td>

                        <Td isNumeric fontFamily="'Rajdhani', sans-serif">
                          {formatTime(entry.StatValue)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Leaderboard;
